import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useSendFeedback, type FeedbackType } from "@/hooks/use-feedback";
import {
  FEEDBACK_PRIORITIES,
  FEEDBACK_PRIORITY_ORDER,
  type FeedbackPriority,
} from "@/lib/feedback-priority";
import { SrIcon } from "@/components/ui/SrIcon";
import FeedbackAttachmentPicker from "./FeedbackAttachmentPicker";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { activeUser, selfUser, isImpersonating } = useImpersonation();
  const location = useLocation();
  const send = useSendFeedback();
  const [type, setType] = useState<FeedbackType>("bug");
  // VIB-2085: ingen default. En forvalgt prioritet blir aldri endret, og da
  // sier feltet ingenting om hvor ille saken faktisk er.
  const [priority, setPriority] = useState<FeedbackPriority | null>(null);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);

  const canSend = !!activeUser && body.trim().length > 0 && !!priority;

  const handleSend = () => {
    if (!canSend || !activeUser) return;
    send.mutate(
      {
        userId: activeUser.id,
        type,
        priority,
        body,
        pageUrl: location.pathname + location.search,
        impersonatedByUserId: isImpersonating ? selfUser?.id ?? null : null,
        attachments,
      },
      {
        onSuccess: () => {
          setBody("");
          setPriority(null);
          setAttachments([]);
          onOpenChange(false);
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Rydd state når dialogen lukkes — ellers ligger gamle filer igjen
      // hvis brukeren åpner dialogen på nytt etter avbryt.
      setAttachments([]);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send tilbakemelding</DialogTitle>
          <DialogDescription>
            Rapportér en bug eller foreslå en forbedring.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setType("bug")}
              className={
                "flex items-center justify-center gap-2 rounded-md border-[1.5px] px-3 py-2 text-sm transition-colors " +
                (type === "bug"
                  ? "border-destructive bg-destructive/5 text-destructive"
                  : "border-border text-muted-foreground hover:border-foreground/30")
              }
            >
              <SrIcon name="alert-triangle" className="w-4 h-4" />
              Bug
            </button>
            <button
              type="button"
              onClick={() => setType("feature")}
              className={
                "flex items-center justify-center gap-2 rounded-md border-[1.5px] px-3 py-2 text-sm transition-colors " +
                (type === "feature"
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border text-muted-foreground hover:border-foreground/30")
              }
            >
              <SrIcon name="sparkles" className="w-4 h-4" />
              Forslag
            </button>
          </div>

          {/*
            VIB-2085: prioritet velges av avsenderen, uavhengig av type. En bug
            kan være kosmetisk, og et forslag kan være det som står mellom noen
            og en hel arbeidsflyt.
          */}
          <div>
            <Label className="text-sm">Hvor hardt rammer dette deg?</Label>
            <div className="mt-1 grid gap-1.5">
              {FEEDBACK_PRIORITY_ORDER.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  aria-pressed={priority === p}
                  className={
                    "rounded-md border-[1.5px] px-3 py-2 text-left text-sm transition-colors " +
                    (priority === p
                      ? "border-foreground bg-muted/60"
                      : "border-border hover:border-foreground/30")
                  }
                >
                  <span className="font-medium">{FEEDBACK_PRIORITIES[p].label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {FEEDBACK_PRIORITIES[p].hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="feedback-body" className="text-sm">
              Beskrivelse
            </Label>
            <Textarea
              id="feedback-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={
                type === "bug"
                  ? "Hva gikk galt? Hvilken side var du på, og hva prøvde du å gjøre?"
                  : "Hva ønsker du? Hvilket problem løser det?"
              }
              rows={5}
              className="mt-1"
            />
            {/*
              VIB-2085: veiledningen står her, ikke bare i placeholderen, som
              forsvinner i det man begynner å skrive. Flere innsendinger har
              vært vage («summerer feil») nettopp fordi det ikke sto noe sted
              hva vi trenger for å kunne reprodusere.
            */}
            <p className="mt-1 text-xs text-muted-foreground">
              Ta med: hva du gjorde, hva du forventet, og hva som faktisk skjedde.
              {type === "bug" && " Nevn prosjektet eller tilbudet det gjelder."}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Sender også URL ({location.pathname}) og brukerinfo automatisk.
            </p>
          </div>

          <div>
            <Label className="text-sm">Vedlegg (valgfritt)</Label>
            <div className="mt-1">
              <FeedbackAttachmentPicker
                files={attachments}
                onChange={setAttachments}
                disabled={send.isPending}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => handleOpenChange(false)} type="button">
              Avbryt
            </Button>
            <Button onClick={handleSend} disabled={!canSend || send.isPending} type="button">
              {send.isPending ? "Sender…" : "Send"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
